import React, { useEffect, useState, startTransition, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { activeTabAtom, sidebarCollapsedAtom } from '../../../../atoms/v3/uiAtoms';
import {
  projectsAtom,
  fetchProjectsAtom,
  projectsLoadingAtom,
  backgroundRefreshingAtom
} from '../../../../atoms/v0/projectAtoms';
import { taskViewModeAtom } from '../../../../atoms/v3/taskAtoms';
import {
  sortedProjectsAtom,
  sortedTimeGroupsAtom,
  visibleProjectIdsAtom,
  visibleTimeGroupsAtom
} from '../../../../atoms/v3/outlineAtoms';
import { triggerCreateProjectAtom, projectsWithStatsAtom } from '../../../../atoms/v3/projectV2Atoms';
import { foldersAtom, fetchFoldersAtom } from '../../../../atoms/v3/folderAtoms';
import { groupProjectsByFolder, getFolderProjectStats } from '../../../../utils/folderUtils';
import { FolderEditModal } from '../folders/FolderEditModal';
import { ProjectDndContext } from '../projects/ProjectDndContext';
import { DraggableDesktopProjectItem } from './DraggableDesktopProjectItem';
import { FolderDroppableArea } from '../folders/FolderDroppableArea';
import { Project } from '../../../../types/project';

/**
 * 桌面大纲组件
 *
 * 根据当前活动标签页显示不同的大纲内容：
 * - 任务页：显示任务分组和项目列表
 * - 聊天页：显示聊天分类
 * - 日历页：显示日历视图选项
 * - 设置页：显示设置分类
 */
export const DesktopOutline: React.FC = () => {
  const { t } = useTranslation(['v3/desktop', 'v3/tasks', 'v3/calendar', 'v3/settings', 'v3/focus', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab] = useAtom(activeTabAtom);
  const [sidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [projects] = useAtom(projectsAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [projectsLoading] = useAtom(projectsLoadingAtom);
  const [backgroundRefreshing] = useAtom(backgroundRefreshingAtom);
  const [viewMode] = useAtom(taskViewModeAtom);
  const [, setTriggerCreateProject] = useAtom(triggerCreateProjectAtom);

  // 文件夹相关状态
  const [folders] = useAtom(foldersAtom);
  const [, fetchFolders] = useAtom(fetchFoldersAtom);
  const [outlineFolderExpanded, setOutlineFolderExpanded] = useState<Record<string, boolean>>({});
  const [editingFolder, setEditingFolder] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Add ref to track if projects already fetched for this tab session
  const hasLoadedProjectsRef = useRef<boolean>(false);
  // 添加延迟显示loading状态
  const [showLoading, setShowLoading] = useState(false);

  // 使用共享的排序后的项目列表和分组信息
  const [sortedProjects] = useAtom(sortedProjectsAtom);
  const [projectsWithStats] = useAtom(projectsWithStatsAtom);
  const [sortedTimeGroups] = useAtom(sortedTimeGroupsAtom);
  const [visibleProjectIds] = useAtom(visibleProjectIdsAtom);
  const [visibleTimeGroups] = useAtom(visibleTimeGroupsAtom);

  const [taskGroups] = useState([
    { id: 'vital-urgent', label: t('v3/tasks:categories.vitalUrgent'), color: 'text-ios-red dark:text-ios-dark-red' },
    { id: 'vital', label: t('v3/tasks:categories.vital'), color: 'text-ios-orange dark:text-ios-dark-orange' },
    { id: 'urgent', label: t('v3/tasks:categories.urgent'), color: 'text-ios-blue dark:text-ios-dark-blue' },
    { id: 'other', label: t('v3/tasks:categories.other'), color: 'text-ios-gray dark:text-ios-dark-gray' },
    { id: 'completed', label: t('v3/tasks:categories.completed'), color: 'text-ios-green dark:text-ios-dark-green' }
  ]);

  // Fetch projects when the 'tasks' tab becomes active.
  useEffect(() => {
    if (activeTab === 'tasks') {
      console.log('[DesktopOutline] Tasks tab is active, ensuring projects are loaded.');
      // Check if we've already fetched projects for this tab session
      if (!hasLoadedProjectsRef.current) {
        hasLoadedProjectsRef.current = true;
        // Wrap in startTransition to ensure it doesn't block UI
        startTransition(() => {
          fetchProjects();
          fetchFolders();
        });
      }
    } else {
      // Reset the flag when tab changes
      hasLoadedProjectsRef.current = false;
    }
  }, [activeTab, fetchProjects]);

  // 用于处理加载状态的延迟显示
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (backgroundRefreshing && projects.length > 0) {
      // 延迟200ms显示loading图标，避免快速完成时的闪烁
      timer = setTimeout(() => {
        setShowLoading(true);
      }, 200);
    } else {
      setShowLoading(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [backgroundRefreshing, projects.length]);

  // 如果侧边栏已折叠，不显示大纲内容
  if (sidebarCollapsed) {
    return null;
  }

  // 切换文件夹展开状态
  const toggleFolderExpanded = (folderId: string) => {
    setOutlineFolderExpanded(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  // 处理编辑文件夹
  const handleEditFolder = (folder: any, event: React.MouseEvent) => {
    event.stopPropagation();
    const stats = getFolderProjectStats(projectsWithStats, folder.id);
    setEditingFolder({ ...folder, projectCount: stats.total });
    setIsEditModalOpen(true);
  };

  // 关闭编辑模态框
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingFolder(null);
  };

  // 根据当前活动标签页渲染不同的大纲内容
  const renderOutlineContent = () => {
    switch (activeTab) {
      case 'tasks':
        if (projectsLoading && projects.length === 0) {
          return (
            <div className="px-4 py-2 text-center text-ios-gray dark:text-ios-dark-gray">
              {t('v3/desktop:loading.projects')}
            </div>
          );
        }

        return (
          <>
            {/* 根据视图模式显示不同的分组 */}
            {viewMode === 'category' && (
              <div className="px-4 py-2">
                <h3 className="text-ios-subhead font-semibold text-ios-gray dark:text-ios-dark-gray mb-2">{t('v3/desktop:sections.taskGroups')}</h3>
                <div className="space-y-1">
                  {taskGroups.map(group => (
                    <button
                      key={group.id}
                      onClick={() => scrollToTaskGroup(group.id)}
                      className={`w-full text-left px-3 py-2 rounded-ios-md text-ios-body ${group.color} hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50`}
                    >
                      {group.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 时间视图分组 */}
            {viewMode === 'time' && (
              <div className="px-4 py-2">
                <h3 className="text-ios-subhead font-semibold text-ios-gray dark:text-ios-dark-gray mb-2">{t('v3/desktop:sections.timeGroups')}</h3>
                <div className="space-y-1">
                  {/* 使用共享的时间分组信息 */}
                  {sortedTimeGroups.map(groupKey => {
                    // 根据时间组类型设置不同的样式
                    let textColor = "text-ios-gray dark:text-ios-dark-gray";
                    if (groupKey === 'today') textColor = "text-ios-red dark:text-ios-dark-red";
                    else if (groupKey === 'tomorrow') textColor = "text-ios-orange dark:text-ios-dark-orange";
                    else if (groupKey === 'future') textColor = "text-ios-blue dark:text-ios-dark-blue";

                    // 获取时间组的显示名称
                    let groupLabel = "";
                    if (groupKey === 'today') groupLabel = t('v3/tasks:categories.today');
                    else if (groupKey === 'tomorrow') groupLabel = t('v3/tasks:categories.tomorrow');
                    else if (groupKey === 'yesterday') groupLabel = t('v3/tasks:categories.yesterday');
                    else if (groupKey === 'future') groupLabel = t('v3/tasks:categories.future');
                    else if (groupKey === 'past') groupLabel = t('v3/tasks:categories.past');
                    else if (groupKey === 'noDate') groupLabel = t('v3/tasks:categories.noDate');

                    return (
                      <button
                        key={groupKey}
                        onClick={() => scrollToTaskGroup(groupKey)}
                        className={`w-full text-left px-3 py-2 rounded-ios-md text-ios-body ${textColor} hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50`}
                      >
                        {groupLabel}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 项目列表 - 仅在项目视图模式下显示 */}
            {viewMode === 'project' && (
              <div className="px-4 py-2 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-ios-subhead font-semibold text-ios-gray dark:text-ios-dark-gray flex items-center">
                    {t('common:projects')}
                    <span className={`ml-2 inline-block transition-opacity duration-300 ${showLoading ? 'opacity-100' : 'opacity-0'}`}>
                      <svg className="animate-spin h-3 w-3 text-ios-gray dark:text-ios-dark-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  </h3>
                  <button
                    onClick={handleCreateProject}
                    className="text-ios-blue dark:text-ios-dark-blue hover:text-ios-blue/80 dark:hover:text-ios-dark-blue/80"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                </div>
                <div className="space-y-1">
                  {/* 使用共享的项目列表 */}
                  {sortedProjects.map(project => (
                    <DraggableDesktopProjectItem
                      key={project.id}
                      project={project}
                      onClick={() => scrollToTaskGroup(project.id)}
                    />
                  ))}

                  {/* 如果可见项目 ID 中包含 'none'，则显示"无项目"选项 */}
                  {visibleProjectIds.includes('none') && (
                    <button
                      onClick={() => scrollToTaskGroup('none')}
                      className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50 truncate"
                    >
                      {t('v3/tasks:categories.noProject')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        );

      case 'projectv2':
        if (projectsLoading && projects.length === 0) {
          return (
            <div className="px-4 py-2 text-center text-ios-gray dark:text-ios-dark-gray">
              {t('v3/desktop:loading.projects')}
            </div>
          );
        }

        return (
          <div className="px-4 py-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-ios-subhead font-semibold text-ios-gray dark:text-ios-dark-gray flex items-center">
                {t('common:projects')}
                <span className={`ml-2 inline-block transition-opacity duration-300 ${showLoading ? 'opacity-100' : 'opacity-0'}`}>
                  <svg className="animate-spin h-3 w-3 text-ios-gray dark:text-ios-dark-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              </h3>
              <button
                onClick={handleCreateProject}
                className="text-ios-blue dark:text-ios-dark-blue hover:text-ios-blue/80 dark:hover:text-ios-dark-blue/80"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
            <div className="space-y-1">
              {/* 按文件夹分组的项目列表 */}
              {(() => {
                const projectGroups = groupProjectsByFolder(projectsWithStats, folders);

                return projectGroups.map((group) => {
                  const folderId = group.folder?.id || 'ungrouped';
                  const isExpanded = outlineFolderExpanded[folderId] !== false; // 默认展开

                  return (
                    <FolderDroppableArea
                      key={folderId}
                      folder={group.folder}
                      isFullArea={true}
                      className="space-y-1"
                    >
                      {/* 文件夹标题 */}
                      <div className="flex items-center justify-between px-2 py-1">
                        <div className="flex-1 flex items-center gap-2">
                          {/* 展开/折叠箭头按钮 */}
                          <button
                            onClick={() => toggleFolderExpanded(folderId)}
                            className="p-1 rounded text-ios-gray2 dark:text-ios-dark-gray2 hover:text-ios-gray1 dark:hover:text-ios-dark-gray1 hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray3 transition-colors"
                          >
                            <svg
                              className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>

                          {/* 文件夹信息（可点击滚动） */}
                          <button
                            onClick={() => scrollToFolder(folderId)}
                            className="flex-1 flex items-center gap-2 text-left text-ios-caption font-medium text-ios-gray2 dark:text-ios-dark-gray2 hover:text-ios-gray1 dark:hover:text-ios-dark-gray1 transition-colors"
                          >
                            {/* 文件夹图标和名称 */}
                            <span className="text-xs">
                              {group.folder ? '📁' : '📋'}
                            </span>
                            <span className="truncate">
                              {group.folder ? group.folder.name : t('v3/projects:folders.ungrouped', '未分组')}
                            </span>

                            {/* 项目数量 */}
                            <span className="text-xs opacity-60">
                              ({group.projects.length})
                            </span>
                          </button>
                        </div>

                        {/* 编辑按钮（仅对真实文件夹显示） */}
                        {group.folder && (
                          <button
                            onClick={(e) => handleEditFolder(group.folder, e)}
                            className="ml-1 p-1 rounded text-ios-gray2 dark:text-ios-dark-gray2 hover:text-ios-gray1 dark:hover:text-ios-dark-gray1 hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray3 transition-colors"
                            title={t('v3/common:actions.edit', '编辑')}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* 文件夹内的项目列表 */}
                      {isExpanded && (
                        <div className="pl-6 space-y-1">
                          {group.projects.map(project => (
                            <DraggableDesktopProjectItem
                              key={project.id}
                              project={project}
                              onClick={() => handleProjectClick(project.id)}
                            />
                          ))}
                        </div>
                      )}
                    </FolderDroppableArea>
                  );
                });
              })()}
            </div>
          </div>
        );

      // 移除对话相关内容

      case 'calendar':
        return (
          <div className="px-4 py-2">
            <h3 className="text-ios-subhead font-semibold text-ios-gray dark:text-ios-dark-gray mb-2">{t('v3/calendar:views.title')}</h3>
            <div className="space-y-1">
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/calendar:views.day')}
              </button>
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/calendar:views.week')}
              </button>
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/calendar:views.month')}
              </button>
            </div>
          </div>
        );

      case 'me':
        return (
          <div className="px-4 py-2">
            <h3 className="text-ios-subhead font-semibold text-ios-gray dark:text-ios-dark-gray mb-2">{t('v3/settings:title')}</h3>
            <div className="space-y-1">
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/settings:sections.profile')}
              </button>
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/settings:sections.appearance')}
              </button>
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/settings:sections.language')}
              </button>
            </div>
          </div>
        );

      case 'focus':
        return (
          <div className="px-4 py-2">
            <h3 className="text-ios-subhead font-semibold text-ios-gray dark:text-ios-dark-gray mb-2">{t('v3/focus:title')}</h3>
            <div className="space-y-1">
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/focus:trends.daily')}
              </button>
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/focus:trends.weekly')}
              </button>
              <button className="w-full text-left px-3 py-2 rounded-ios-md text-ios-body text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4/50">
                {t('v3/focus:trends.productivity')}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // 滚动到指定任务分组
  const scrollToTaskGroup = (groupId: string) => {
    const element = document.getElementById(groupId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 滚动到文件夹
  const scrollToFolder = (folderId: string) => {
    const element = document.getElementById(`folder-${folderId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 滚动到项目
  const scrollToProject = (projectId: string) => {
    const element = document.getElementById(`project-${projectId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 处理项目点击
  const handleProjectClick = (projectId: string) => {
    // 在 ProjectV2 页面中，点击项目会滚动到对应的项目
    if (activeTab === 'projectv2') {
      scrollToProject(projectId);
    } else if (viewMode === 'project') {
      // 在项目视图模式下，点击项目会滚动到对应的项目组
      scrollToTaskGroup(projectId);
    } else {
      // 在其他视图模式下，点击项目会导航到项目详情页
      navigate(`/v3/projects/${projectId}`);
    }
  };

  // 处理创建项目
  const handleCreateProject = () => {
    // 触发全局创建项目状态
    setTriggerCreateProject(prev => prev + 1);
  };

  return (
    <ProjectDndContext>
      <div className="py-2 desktop-outline" data-task-list="true">
        {renderOutlineContent()}

        {/* 文件夹编辑模态框 */}
        <FolderEditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          folder={editingFolder}
          projectCount={editingFolder?.projectCount || 0}
        />
      </div>
    </ProjectDndContext>
  );
};


